import React, { useCallback } from 'react';

import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import Layout from '../../containers/Layout';
import Sidebar from '../../components/AddOrEditPage/Sidebar';
import Lender from '../../components/AddOrEditPage/AddOrEditLender/Sections/Lender';
import Users from '../../components/AddOrEditPage/AddOrEditLender/Sections/Users';
import Loans from '../../components/AddOrEditPage/AddOrEditLender/Sections/Loans';
import Settings from '../../components/AddOrEditPage/AddOrEditLender/Sections/Settings';
import LienHolders from '../../components/AddOrEditPage/AddOrEditLender/Sections/LienHolders';
import EditLienHolder from '../../components/AddOrEditPage/AddOrEditLender/Sections/EditLienHolder';
import Documents from '../../components/AddOrEditPage/AddOrEditLender/Sections/Documents';
import Document from '../../components/AddOrEditPage/AddOrEditLender/Sections/Document';
import BankAccount from '../../components/AddOrEditPage/AddOrEditLender/Sections/BankAccount';
import BillPayments from '../../components/AddOrEditPage/AddOrEditLender/Sections/BillPayments';
import Insurances from '../../components/AddOrEditPage/AddOrEditLender/Sections/Insurances';
import Insurance from '../../components/AddOrEditPage/AddOrEditLender/Sections/Insurance';
import Distributions from '../../components/AddOrEditPage/AddOrEditLender/Sections/Distributions';
import Contacts from '../../components/AddOrEditPage/AddOrEditLender/Sections/Contacts';
import Contact from '../../components/AddOrEditPage/AddOrEditLender/Sections/Contact';
import LoanGroups from '../../components/AddOrEditPage/AddOrEditLender/Sections/LoanGroups';
import LoanGroup from '../../components/AddOrEditPage/AddOrEditLender/Sections/LoanGroup';

import LenderService from '../../services/LenderService';
import useAddQueryParam from '../../hooks/useAddQueryParams';
import { useServicerStore } from '../../store';

const defaultSvgPath = (
  <path d="M7.3 8.7c-.4-.4-.4-1 0-1.4l7-7c.4-.4 1-.4 1.4 0 .4.4.4 1 0 1.4l-7 7c-.4.4-1 .4-1.4 0ZM7.3 14.7c-.4-.4-.4-1 0-1.4l7-7c.4-.4 1-.4 1.4 0 .4.4.4 1 0 1.4l-7 7c-.4.4-1 .4-1.4 0ZM.3 9.7c-.4-.4-.4-1 0-1.4l7-7c.4-.4 1-.4 1.4 0 .4.4.4 1 0 1.4l-7 7c-.4.4-1 .4-1.4 0Z" />
);

const sections = [
  {
    title: 'Lender',
    svgPath: (
      <>
        <path d="M10.59 7.658 8 5.5 5.41 7.658A.25.25 0 0 1 5 7.466V0h6v7.466a.25.25 0 0 1-.41.192Z" />
        <path d="M14 16H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h2v2H2v12h12V2h-2V0h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2Z" />
      </>
    ),
  },
  {
    title: 'Contacts',
    svgPath: defaultSvgPath,
    activeOn: 'Contact',
  },
  {
    title: 'Users',
    svgPath: (
      <path d="M12.311 9.527c-1.161-.393-1.85-.825-2.143-1.175A3.991 3.991 0 0012 5V4c0-2.206-1.794-4-4-4S4 1.794 4 4v1c0 1.406.732 2.639 1.832 3.352-.292.35-.981.782-2.142 1.175A3.942 3.942 0 001 13.26V16h14v-2.74c0-1.69-1.081-3.19-2.689-3.733zM6 4c0-1.103.897-2 2-2s2 .897 2 2v1c0 1.103-.897 2-2 2s-2-.897-2-2V4zm7 10H3v-.74c0-.831.534-1.569 1.33-1.838 1.845-.624 3-1.436 3.452-2.422h.436c.452.986 1.607 1.798 3.453 2.422A1.943 1.943 0 0113 13.26V14z" />
    ),
  },
  {
    title: 'Loans',
    svgPath: (
      <path d="M5 9h11v2H5V9zM0 9h3v2H0V9zm5 4h6v2H5v-2zm-5 0h3v2H0v-2zm5-8h7v2H5V5zM0 5h3v2H0V5zm5-4h11v2H5V1zM0 1h3v2H0V1z" />
    ),
  },
  {
    title: 'Loan Groups',
    svgPath: defaultSvgPath,
    activeOn: 'Loan Group',
  },
  {
    title: 'Insurances',
    svgPath: (
      <path d="M14.14 9.585h-.002a2.5 2.5 0 0 1-2 4.547 6.91 6.91 0 0 1-6.9 1.165 4.436 4.436 0 0 0 1.343-1.682c.365.087.738.132 1.113.135a4.906 4.906 0 0 0 2.924-.971 2.5 2.5 0 0 1 3.522-3.194Zm-4.015-7.397a7.023 7.023 0 0 1 4.47 5.396 4.5 4.5 0 0 0-1.7-.334c-.15.002-.299.012-.447.03a5.027 5.027 0 0 0-2.723-3.078 2.5 2.5 0 1 1 .4-2.014ZM4.663 10.5a2.5 2.5 0 1 1-3.859-.584 6.888 6.888 0 0 1-.11-1.166c0-2.095.94-4.08 2.56-5.407.093.727.364 1.419.788 2.016A4.97 4.97 0 0 0 2.694 8.75c.003.173.015.345.037.516A2.49 2.49 0 0 1 4.663 10.5Z" />
    ),
    activeOn: 'Insurance',
  },
  {
    title: 'Distributions',
    svgPath: defaultSvgPath,
  },
  {
    title: 'Ledger',
    svgPath: (
      <path d="M10 16h4c.6 0 1-.4 1-.998V6.016c0-.3-.1-.6-.4-.8L8.6.226c-.4-.3-.9-.3-1.3 0l-6 4.992c-.2.2-.3.5-.3.799v8.986C1 15.6 1.4 16 2 16h4c.6 0 1-.4 1-.998v-2.996h2v2.996c0 .599.4.998 1 .998Zm-4-5.99c-.6 0-1 .399-1 .998v2.995H3V6.515L8 2.32l5 4.194v7.488h-2v-2.995c0-.6-.4-.999-1-.999H6Z" />
    ),
  },
  {
    title: 'Reports',
    svgPath: defaultSvgPath,
  },
  {
    title: 'Bill Payments',
    svgPath: defaultSvgPath,
  },
  {
    title: 'Bank Account',
    svgPath: defaultSvgPath,
  },
  {
    title: 'Lien Holders',
    svgPath: defaultSvgPath,
    activeOn: 'Lien Holder',
  },
  {
    title: 'Documents',
    svgPath: defaultSvgPath,
    activeOn: 'Document',
  },
  {
    title: 'Settings',
    svgPath: defaultSvgPath,
  },
];

export default function AddOrEditLender({ isEdit }) {
  const userRole = useServicerStore((state) => state.userRole);
  const isSuperAdmin = userRole === 'superAdmin';
  const isLender = userRole === 'lender';

  const { id } = useParams();

  const { currentQueryParam, addQueryParam } = useAddQueryParam('tab');

  const {
    data: lender,
    isFetching,
    refetch: refetchLender,
  } = useQuery({
    queryKey: ['lender', id],
    queryFn: () => LenderService.getLender(id),
    enabled: !!id,
  });

  const switchToNextSection = useCallback(() => {
    const sectionIndex = sections.findIndex(
      (section) => section.title === currentQueryParam
    );

    if (sectionIndex < sections.length - 1) {
      addQueryParam('tab', sections[0].title);
    }
  }, [addQueryParam, currentQueryParam]);

  const currentSections = isSuperAdmin
    ? sections.filter((section) => section.title !== 'Distributions')
    : sections.filter(
        (section) =>
          !['Ledger', 'Bill Payments', 'Documents'].includes(section.title)
      );

  return (
    <Layout>
      <div className="w-full max-w-9xl mx-auto md:h-[calc(100dvh-64px)] overflow-hidden relative">
        <div className="sm:flex sm:justify-between sm:items-center mb-5 h-full">
          <div className="mb-4 sm:mb-0 w-screen md:w-full h-full relative">
            <div className="md:h-16 relative z-30">
              <h1 className="px-4 sm:px-6 text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold bg-slate-100 dark:bg-slate-900 pt-6 z-20 after:absolute after:-top-1 after:h-2 after:w-full after:left-0 after:bg-slate-100 after:dark:bg-slate-900">
                {lender?.name || 'New Lender ✨'}
              </h1>
            </div>
            <div className="px-4 sm:px-6  flex md:flex-row flex-col h-full md:h-[calc(100%-64px)]">
              <Sidebar isEdit={!!id} sections={currentSections} />
              <div className="md:ml-8 2xl:mx-8 flex-1 overflow-auto pt-10">
                <div
                  style={{
                    display: currentQueryParam === 'Lender' ? 'block' : 'none',
                  }}
                >
                  <Lender
                    isVisible={currentQueryParam === 'Lender'}
                    isEdit={isEdit}
                    switchToNextSection={switchToNextSection}
                    lender={lender}
                    readOnly={!isSuperAdmin}
                  />
                </div>
                {currentQueryParam === 'Contacts' && id && (
                  <Contacts lenderId={id} />
                )}
                {currentQueryParam === 'Contact' && id && (
                  <Contact lenderId={id} />
                )}
                <div
                  style={{
                    display:
                      currentQueryParam === 'Users' && id ? 'block' : 'none',
                  }}
                >
                  <Users
                    isVisible={currentQueryParam === 'Users' && id}
                    isEdit={isEdit}
                    switchToNextSection={switchToNextSection}
                    lender={lender}
                    isFetching={isFetching}
                    refetchLender={refetchLender}
                  />
                </div>
                <div
                  style={{
                    display:
                      currentQueryParam === 'Loans' && id ? 'block' : 'none',
                  }}
                >
                  <Loans
                    isVisible={currentQueryParam === 'Loans' && id}
                    isEdit={isEdit}
                    lenderId={id}
                  />
                </div>
                {currentQueryParam === 'Loan Groups' && id && (
                  <LoanGroups lenderId={id} isLender={isLender} />
                )}
                {currentQueryParam === 'Loan Group' && id && (
                  <LoanGroup lenderId={id} />
                )}
                {currentQueryParam === 'Insurances' && id && (
                  <Insurances lenderId={id} isLender={isLender} />
                )}
                {currentQueryParam === 'Insurance' && id && (
                  <Insurance lenderId={id} />
                )}
                {currentQueryParam === 'Distributions' && id && isLender && (
                  <Distributions lenderId={id} />
                )}
                {currentQueryParam === 'Lien Holders' && id && (
                  <LienHolders lenderId={id} />
                )}
                {currentQueryParam === 'Lien Holder' && id && (
                  <EditLienHolder lenderId={id} />
                )}
                {currentQueryParam === 'Bill Payments' &&
                  id &&
                  isSuperAdmin && <BillPayments lenderId={id} />}
                {currentQueryParam === 'Bank Account' && id && (
                  <BankAccount
                    lenderId={id}
                    lender={lender}
                    refetch={refetchLender}
                  />
                )}
                {currentQueryParam === 'Documents' && id && isSuperAdmin && (
                  <Documents lenderId={id} />
                )}
                {currentQueryParam === 'Document' && id && (
                  <div>
                    <Document lenderId={id} />
                  </div>
                )}
                <div
                  style={{
                    display:
                      currentQueryParam === 'Settings' && id ? 'block' : 'none',
                  }}
                >
                  <Settings
                    isVisible={currentQueryParam === 'Settings' && id}
                    isEdit={isEdit}
                    lenderId={id}
                    lender={lender}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
